import cx from 'classnames';
import { useEffect } from 'react';
import { getClickAccessibilityProps } from '../../../utils/accessibilityUtils';
import useBuildVariantProps from '../../../../modules/theme/useBuildVariantProps';
import { Icon } from '../../../atoms';
import { WrapTextNodes } from '../../../utils/WrapTextNodes';
import { useSelectContext } from '../Select.Context';
import { checkIsSelected } from '../Select.utils';
import styles from './Option.module.scss';
import type { OptionProps } from './Option.types';

/**
 * Functional component representing an option within a select dropdown.
 *
 * @param props - Props for configuring the Option component.
 * @returns Element representing the option.
 */
const Option = (props: OptionProps) => {
  const {
    disabled = false,
    leadingIcon,
    trailingIcon,
    value,
    className,
    children,
    closeSelectOptions, // This prop is for internal use, not to be set by the user
    ...restProp
  } = props;

  // Accessing Select context to manage selection state
  const { selectedValue, updateSelectedValue, registerOption } = useSelectContext();
  const isSelected = checkIsSelected(String(value), selectedValue);

  // Register this option's value-label pair
  useEffect(() => {
    registerOption(value, children);
  }, [value, children, registerOption]);

  let leadingIconComponent, trailingIconComponent, content;

  // Render leading icon if provided
  if (leadingIcon) {
    leadingIconComponent = <div className={cx(styles.leadingIcon)}>{leadingIcon}</div>;
  }

  // Render trailing icon if provided
  const trailingIconView = trailingIcon ?? (
    <Icon
      icon='Check'
      style='Outlined'
      size='Small'
    />
  );

  if(isSelected) {
    trailingIconComponent = trailingIconView;
  }

  // Render children content if provided
  if (children) {
    content = <div className={cx(styles.content)}>
      <WrapTextNodes whiteSpaceNoWrap={true} className={styles.text}>{children}</WrapTextNodes>
    </div>;
  }

  /**
   * Handles click event on the option.
   * Updates the selected value if the option is not disabled.
   */
  const handleClick = () => {
    if (!disabled) {
      updateSelectedValue(value);
      closeSelectOptions?.();
    }
  };

  const variantProps = useBuildVariantProps(
    'select-list-item',
    {
      type: isSelected ? 'selected' : 'unselected',
    },
    {
      disabled,
    }
  );

  const accessibilityProps = getClickAccessibilityProps({
    actions: {
      onClick: handleClick,
    },
    addIndex: true,
    role: 'option',
  });

  return (
    <li
      {...restProp}
      className={cx(
        styles.option,
        { [styles.selected]: isSelected },
        className
      )}
      onClick={handleClick}
      {...accessibilityProps}
      {...variantProps}
    >
      {leadingIconComponent}
      {content}
      {trailingIconComponent}
    </li>
  );
};

export default Option;
